// src/services/api.js - Client API centralisé avec gestion des tokens
import axios from 'axios';
import toast from 'react-hot-toast';

const BASE_URL = process.env.REACT_APP_API_URL || '/api';

// Instance Axios principale
const api = axios.create({
  baseURL: BASE_URL,
  timeout: 15000,
  headers: { 'Content-Type': 'application/json' },
});

// ============================================
// INTERCEPTEURS
// ============================================

// Requête: Injecter le token JWT automatiquement
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Réponse: Gérer le refresh token automatique
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(({ resolve, reject }) => error ? reject(error) : resolve(token));
  failedQueue = [];
};

api.interceptors.response.use(
  (response) => response.data,

  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      if (error.response?.data?.code === 'TOKEN_EXPIRED') {
        if (isRefreshing) {
          return new Promise((resolve, reject) => failedQueue.push({ resolve, reject }))
            .then(token => { originalRequest.headers.Authorization = `Bearer ${token}`; return api(originalRequest); });
        }

        originalRequest._retry = true;
        isRefreshing = true;

        try {
          const refreshToken = localStorage.getItem('refresh_token');
          if (!refreshToken) throw new Error('No refresh token');

          const { data } = await axios.post(`${BASE_URL}/auth/refresh`, { refresh_token: refreshToken });
          const newToken = data.access_token;

          localStorage.setItem('access_token', newToken);
          api.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
          processQueue(null, newToken);
          try {
            const { socketService } = require('./socket');
            if (socketService.socket) {
              socketService.socket.auth = { token: newToken };
              if (!socketService.socket.connected) socketService.socket.connect();
            }
          } catch(e) {}

          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return api(originalRequest);
        } catch (refreshError) {
          processQueue(refreshError, null);
          // Session expirée → déconnecter
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          window.location.href = '/login';
          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
        }
      }
    }

    // Afficher l'erreur à l'utilisateur
    const message = error.response?.data?.error || error.response?.data?.errors?.[0] || 'Une erreur est survenue';
    const isRefreshEndpoint = error.config?.url?.includes('/auth/refresh');
    if (error.response?.status !== 401 && !isRefreshEndpoint) {
      toast.error(message);
    }

    return Promise.reject(error.response?.data || error);
  }
);

// ============================================
// SERVICES API
// ============================================

export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  refresh: (refresh_token) => api.post('/auth/refresh', { refresh_token }),
  verifyPhone: (data) => api.post('/auth/verify-phone', data),
  resendOtp: (data) => api.post('/auth/resend-otp', data),
  forgotPassword: (data) => api.post('/auth/forgot-password', data),
  resetPassword: (data) => api.post('/auth/reset-password', data),
  login: (data) => api.post('/auth/login', data),
  verify2FA: (data) => api.post('/auth/2fa/verify', data),
  setup2FA: () => api.post('/auth/2fa/setup'),
  enable2FA: (data) => api.post('/auth/2fa/enable', data),
  logout: () => api.post('/auth/logout'),
};

export const tontineAPI = {
  getAll: () => api.get('/tontines'),
  getOne: (id) => api.get(`/tontines/${id}`),
  create: (data) => api.post('/tontines', data),
  invite: (id, user_id) => api.post(`/tontines/${id}/invite`, { user_id }),
  join: (id) => api.post(`/tontines/${id}/join`),
  start: (id) => api.post(`/tontines/${id}/start`),
  pay: (id, pin) => api.post(`/tontines/${id}/pay`, { pin }),
};

export const paymentAPI = {
  getWallet: () => api.get('/payments/wallet'),
  sendPayment: (data) => api.post('/payments/send', data),
  requestPayment: (data) => api.post('/payments/request', data),
  splitBill: (data) => api.post('/payments/split', data),
  getTransactions: (params) => api.get('/payments/history', { params }),
};

export const messageAPI = {
  getConversations: () => api.get('/messages/conversations'),
  createConversation: (data) => api.post('/messages/conversations', data),
  getMessages: (conversationId, params) => api.get(`/messages/conversations/${conversationId}/messages`, { params }),
  sendMessage: (conversationId, data) => api.post(`/messages/conversations/${conversationId}/messages`, data),
  searchUsers: (q) => api.get('/messages/users/search', { params: { q } }),
};

export default api;
